import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig as ShadcnChartConfig,
} from '@/components/ui/chart';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import {
  Table as ShadTable,
  TableBody as ShadTableBody,
  TableCell as ShadTableCell,
  TableHead,
  TableHeader,
  TableRow as ShadTableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import HeaderSort from '../components/third-party/table/HeaderSort';
import Pagination from '../components/third-party/table/Pagination';
import { IconRefresh, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';

interface TopicInfo {
  name: string;
  fullName: string;
}

interface SubscriptionInfo {
  id: string;
  fullName: string;
  topic: string;
  backlog?: number;
  oldestMessageAge?: number;
  backlogStatus?: 'healthy' | 'warning' | 'alert';
}

interface HistoryDataPoint {
  timestamp: number;
  value: number;
}

interface SubscriptionHistory {
  backlog: HistoryDataPoint[];
  oldestMessageAge: HistoryDataPoint[];
}

interface DlqMessageInfo {
  messageId: string;
  data: string;
  attributes: Record<string, string>;
  publishTime: string;
  deliveryAttempt: number;
  orderingKey?: string;
  ackId: string;
  subscriptionName: string;
}

interface DlqSubscriptionInfo {
  id: string;
  fullName: string;
  originalTopic: string;
  messageCount: number;
}

export function PubsubPage() {
  const { hasPageAccess, hasWriteAccess } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [topics, setTopics] = useState<TopicInfo[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionInfo[]>([]);
  const [activeTab, setActiveTab] = useState<'topics' | 'subscriptions' | 'dlq'>('subscriptions');
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<Set<string>>(new Set());
  const [subscriptionHistories, setSubscriptionHistories] = useState<Record<string, SubscriptionHistory>>({});
  const [loadingHistories, setLoadingHistories] = useState<Set<string>>(new Set());
  const [dashboardStatus, setDashboardStatus] = useState<{
    overallStatus?: 'healthy' | 'warning' | 'alert';
    alertCount?: number;
    warningCount?: number;
    healthyCount?: number;
    lastChecked?: string;
  } | null>(null);
  const [dlqSubscriptions, setDlqSubscriptions] = useState<DlqSubscriptionInfo[]>([]);
  const [selectedDlq, setSelectedDlq] = useState<string | null>(null);
  const [dlqMessages, setDlqMessages] = useState<DlqMessageInfo[]>([]);
  const [loadingDlqMessages, setLoadingDlqMessages] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedMessage, setSelectedMessage] = useState<DlqMessageInfo | null>(null);
  const [messageDetailOpen, setMessageDetailOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState<{
    open: boolean;
    oldMessageId: string;
    newMessageId: string;
    nextMessage?: DlqMessageInfo;
  } | null>(null);

  const selectedMessageIds = useMemo(() => {
    return new Set(Object.keys(rowSelection).filter((id) => rowSelection[id]));
  }, [rowSelection]);

  const handleRefresh = async () => {
    if (!hasPageAccess('monitoring')) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.get('/monitoring/pubsub/dashboard');
      const data = resp.data?.data;
      
      if (data) {
        setTopics(Array.isArray(data.topics) ? data.topics : []);
        setSubscriptions(Array.isArray(data.subscriptions) ? data.subscriptions : []);
      }
      setLastUpdatedAt(new Date());
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Failed to load Pub/Sub data';
      setError(errorMessage);
      console.error('Error loading Pub/Sub data:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboardStatus = async () => {
    try {
      const resp = await api.get('/monitoring/pubsub/status');
      const data = resp.data?.data;
      if (data) {
        setDashboardStatus(data);
      }
    } catch (e: any) {
      console.error('Error loading dashboard status:', e);
    }
  };

  const fetchDlqSubscriptions = async () => {
    try {
      const resp = await api.get('/monitoring/pubsub/dlq/subscriptions');
      const data = resp.data?.data;
      if (data) {
        setDlqSubscriptions(Array.isArray(data) ? data : []);
      }
    } catch (e: any) {
      console.error('Error loading DLQ subscriptions:', e);
    }
  };

  const fetchDlqMessages = async (subscriptionId: string) => {
    setLoadingDlqMessages(true);
    setRowSelection({});
    try {
      const resp = await api.get(`/monitoring/pubsub/dlq/subscriptions/${subscriptionId}/messages?maxMessages=100`);
      const data = resp.data?.data;
      if (data) {
        setDlqMessages(Array.isArray(data) ? data : []);
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Failed to load DLQ messages';
      setError(errorMessage);
      console.error('Error loading DLQ messages:', e);
    } finally {
      setLoadingDlqMessages(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!selectedDlq || selectedMessageIds.size === 0) return;

    setIsProcessing(true);
    try {
      const messagesToAck = dlqMessages.filter(msg => selectedMessageIds.has(msg.messageId));
      const ackIds = messagesToAck.map(msg => msg.ackId);

      await api.post(`/monitoring/pubsub/dlq/subscriptions/${selectedDlq}/acknowledge`, {
        ackIds,
      });

      toast.success(`Successfully acknowledged ${ackIds.length} message(s)`);
      await fetchDlqMessages(selectedDlq);
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Failed to acknowledge messages';
      toast.error(errorMessage);
      console.error('Error acknowledging messages:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReprocess = async () => {
    if (!selectedDlq || selectedMessageIds.size === 0) return;

    const dlqInfo = dlqSubscriptions.find(d => d.id === selectedDlq);
    if (!dlqInfo) {
      toast.error('Could not determine original topic');
      return;
    }

    setIsProcessing(true);
    try {
      const messagesToReprocess = dlqMessages.filter(msg => selectedMessageIds.has(msg.messageId));

      const resp = await api.post(`/monitoring/pubsub/dlq/subscriptions/${selectedDlq}/reprocess`, {
        messages: messagesToReprocess.map(msg => ({
          ackId: msg.ackId,
          messageId: msg.messageId,
          data: msg.data,
          attributes: msg.attributes,
          orderingKey: msg.orderingKey,
        })),
        originalTopic: dlqInfo.originalTopic,
      });

      const result = resp.data?.data;
      if (result) {
        const message = `Reprocessed ${result.succeeded} message(s)` +
          (result.failed > 0 ? `, ${result.failed} failed` : '');
        if (result.failed > 0) {
          toast.error(message);
        } else {
          toast.success(message);
        }
      }

      await fetchDlqMessages(selectedDlq);
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Failed to reprocess messages';
      toast.error(errorMessage);
      console.error('Error reprocessing messages:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    handleRefresh();
    fetchDashboardStatus();
    fetchDlqSubscriptions();
    const statusInterval = setInterval(fetchDashboardStatus, 30000);
    return () => clearInterval(statusInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: these functions are not wrapped in useCallback
  }, []);

  const getStatusBadgeVariant = (status?: 'healthy' | 'warning' | 'alert'): 'default' | 'destructive' | 'secondary' | 'outline' => {
    switch (status) {
      case 'alert':
        return 'destructive';
      case 'warning':
        return 'outline';
      case 'healthy':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status?: 'healthy' | 'warning' | 'alert') => {
    switch (status) {
      case 'alert':
        return 'Alert';
      case 'warning':
        return 'Warning';
      case 'healthy':
        return 'Healthy';
      default:
        return 'Unknown';
    }
  };

  const formatAge = (seconds?: number): string => {
    if (seconds === undefined || seconds < 0) return '—';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDataPreview = (base64Data: string): string => {
    try {
      const decoded = atob(base64Data);
      if (decoded.length > 100) {
        return decoded.substring(0, 100) + '...';
      }
      return decoded;
    } catch {
      return '[Binary data]';
    }
  };

  const formatBytes = (base64Data: string): string => {
    try {
      const decoded = atob(base64Data);
      return `${decoded.length} bytes`;
    } catch {
      return 'Unknown';
    }
  };

  const decodeMessageData = (base64Data: string): string => {
    try {
      const decoded = atob(base64Data);
      try {
        const json = JSON.parse(decoded);
        return JSON.stringify(json, null, 2);
      } catch {
        return decoded;
      }
    } catch {
      return '[Unable to decode]';
    }
  };

  const dlqMessageColumnDefs = useMemo<ColumnDef<DlqMessageInfo>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all on page"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select message ${row.original.messageId}`}
          />
        ),
        enableSorting: false,
        size: 48,
      },
      {
        accessorKey: 'messageId',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            Message ID
            <HeaderSort column={column} sort />
          </div>
        ),
        size: 200,
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">
            {getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'publishTime',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            Publish Time
            <HeaderSort column={column} sort />
          </div>
        ),
        size: 180,
        cell: ({ getValue }) => new Date(getValue() as string).toLocaleString(),
      },
      {
        accessorKey: 'deliveryAttempt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            Attempts
            <HeaderSort column={column} sort />
          </div>
        ),
        size: 100,
      },
      {
        accessorKey: 'data',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            Data Preview
            <HeaderSort column={column} sort />
          </div>
        ),
        size: 300,
        cell: ({ getValue }) => (
          <span className="truncate font-mono text-xs">
            {formatDataPreview(getValue() as string)}
          </span>
        ),
      },
      {
        id: 'size',
        accessorFn: (row) => formatBytes(row.data),
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            Size
            <HeaderSort column={column} sort />
          </div>
        ),
        size: 100,
      },
      {
        accessorKey: 'orderingKey',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            Ordering Key
            <HeaderSort column={column} sort />
          </div>
        ),
        size: 150,
        cell: ({ getValue }) => (getValue() as string | undefined) || '—',
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMessage(row.original);
              setMessageDetailOpen(true);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  );

  const MessageDetailModal = () => {
    if (!selectedMessage) return null;

    const decodedData = decodeMessageData(selectedMessage.data);

    return (
      <Dialog open={messageDetailOpen} onOpenChange={setMessageDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <h4 className="text-sm font-semibold mb-1">Message ID</h4>
              <p className="font-mono text-sm break-all">{selectedMessage.messageId}</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-1">Publish Time</h4>
              <p className="text-sm">{new Date(selectedMessage.publishTime).toLocaleString()}</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-1">Delivery Attempts</h4>
              <p className="text-sm">{selectedMessage.deliveryAttempt}</p>
            </div>

            {selectedMessage.orderingKey && (
              <div>
                <h4 className="text-sm font-semibold mb-1">Ordering Key</h4>
                <p className="font-mono text-sm">{selectedMessage.orderingKey}</p>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold mb-1">Attributes</h4>
              {Object.keys(selectedMessage.attributes).length === 0 ? (
                <p className="text-sm text-muted-foreground">No attributes</p>
              ) : (
                <ShadTable>
                  <ShadTableBody>
                    {Object.entries(selectedMessage.attributes).map(([key, value]) => (
                      <ShadTableRow key={key}>
                        <ShadTableCell className="font-mono font-semibold">{key}</ShadTableCell>
                        <ShadTableCell className="font-mono break-all">{value}</ShadTableCell>
                      </ShadTableRow>
                    ))}
                  </ShadTableBody>
                </ShadTable>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-1">Message Data</h4>
              <div className="rounded-lg bg-muted p-3 font-mono text-xs whitespace-pre-wrap break-all max-h-96 overflow-auto">
                {decodedData}
              </div>
              <span className="mt-1 block text-xs text-muted-foreground">
                Size: {formatBytes(selectedMessage.data)}
              </span>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-1">Ack ID</h4>
              <p className="font-mono text-[0.7rem] break-all">{selectedMessage.ackId}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageDetailOpen(false)}>Close</Button>
            <Button
              variant="outline"
              onClick={async () => {
                if (!selectedMessage || !selectedDlq) return;
                try {
                  setIsProcessing(true);
                  await api.post(`/monitoring/pubsub/dlq/subscriptions/${selectedDlq}/acknowledge`, {
                    ackIds: [selectedMessage.ackId],
                  });
                  toast.success('Message acknowledged and removed from DLQ');
                  setMessageDetailOpen(false);
                  await fetchDlqMessages(selectedDlq);
                } catch (error: any) {
                  toast.error(`Failed to acknowledge: ${error.response?.data?.message || error.message}`);
                } finally {
                  setIsProcessing(false);
                }
              }}
              disabled={isProcessing}
            >
              Acknowledge (Discard)
            </Button>
            <Button
              onClick={async () => {
                if (!selectedMessage || !selectedDlq) return;
                
                const dlqInfo = dlqSubscriptions.find(d => d.id === selectedDlq);
                if (!dlqInfo) {
                  toast.error('Could not determine original topic');
                  return;
                }
                
                try {
                  setIsProcessing(true);
                  
                  const currentIndex = dlqMessages.findIndex(m => m.messageId === selectedMessage.messageId);
                  const nextMessage = currentIndex >= 0 && currentIndex < dlqMessages.length - 1 
                    ? dlqMessages[currentIndex + 1] 
                    : undefined;
                  
                  const resp = await api.post(`/monitoring/pubsub/dlq/subscriptions/${selectedDlq}/reprocess`, {
                    messages: [{
                      ackId: selectedMessage.ackId,
                      messageId: selectedMessage.messageId,
                      data: selectedMessage.data,
                      attributes: selectedMessage.attributes,
                      orderingKey: selectedMessage.orderingKey,
                    }],
                    originalTopic: dlqInfo.originalTopic,
                  });
                  
                  const result = resp.data?.data;
                  if (result?.reprocessedMessages && result.reprocessedMessages.length > 0) {
                    const reprocessed = result.reprocessedMessages[0];
                    
                    setReprocessResult({
                      open: true,
                      oldMessageId: reprocessed.oldMessageId,
                      newMessageId: reprocessed.newMessageId,
                      nextMessage: nextMessage,
                    });
                  } else {
                    toast.success('Message reprocessed successfully');
                  }
                  
                  setMessageDetailOpen(false);
                  await fetchDlqMessages(selectedDlq);
                } catch (error: any) {
                  toast.error(`Failed to reprocess: ${error.response?.data?.message || error.message}`);
                } finally {
                  setIsProcessing(false);
                }
              }}
              disabled={isProcessing}
            >
              Reprocess
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const ReprocessResultDialog = () => {
    if (!reprocessResult) return null;

    return (
      <Dialog open={reprocessResult.open} onOpenChange={() => setReprocessResult(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Message Reprocessed Successfully</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <h4 className="text-sm font-semibold mb-1">Original DLQ Message ID</h4>
              <p className="font-mono text-sm break-all">{reprocessResult.oldMessageId}</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-green-600 mb-1">
                New Message ID (Published to Topic)
              </h4>
              <p className="font-mono text-sm text-green-600 break-all">
                {reprocessResult.newMessageId}
              </p>
              <span className="mt-1 block text-xs text-muted-foreground">
                Use this ID to search application logs
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReprocessResult(null)}>Close</Button>
            {reprocessResult.nextMessage && (
              <Button
                onClick={() => {
                  setSelectedMessage(reprocessResult.nextMessage!);
                  setMessageDetailOpen(true);
                  setReprocessResult(null);
                }}
              >
                Next Message
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const fetchSubscriptionHistory = async (subscriptionId: string) => {
    if (subscriptionHistories[subscriptionId] || loadingHistories.has(subscriptionId)) {
      return;
    }

    const subscription = subscriptions.find(s => s.id === subscriptionId);
    const fullName = subscription?.fullName;

    setLoadingHistories((prev) => new Set(prev).add(subscriptionId));
    try {
      const url = fullName 
        ? `/monitoring/pubsub/subscriptions/${subscriptionId}/history?hours=1&fullName=${encodeURIComponent(fullName)}`
        : `/monitoring/pubsub/subscriptions/${subscriptionId}/history?hours=1`;
      const resp = await api.get(url);
      const data = resp.data?.data;
      if (data) {
        setSubscriptionHistories((prev) => ({
          ...prev,
          [subscriptionId]: data,
        }));
      }
    } catch (e: any) {
      console.error(`Error loading history for ${subscriptionId}:`, e);
    } finally {
      setLoadingHistories((prev) => {
        const next = new Set(prev);
        next.delete(subscriptionId);
        return next;
      });
    }
  };

  const toggleSubscriptionExpanded = (subscriptionId: string) => {
    setExpandedSubscriptions((prev) => {
      const next = new Set(prev);
      if (next.has(subscriptionId)) {
        next.delete(subscriptionId);
      } else {
        next.add(subscriptionId);
        fetchSubscriptionHistory(subscriptionId);
      }
      return next;
    });
  };

  const sortedSubscriptions = useMemo(() => {
    return [...subscriptions].sort((a, b) => {
      const statusPriority: Record<string, number> = {
        alert: 0,
        warning: 1,
        healthy: 2,
      };
      const aPriority = statusPriority[a.backlogStatus || ''] ?? 3;
      const bPriority = statusPriority[b.backlogStatus || ''] ?? 3;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      const aBacklog = a.backlog ?? 0;
      const bBacklog = b.backlog ?? 0;
      if (aBacklog !== bBacklog) {
        return bBacklog - aBacklog;
      }
      
      return a.id.localeCompare(b.id);
    });
  }, [subscriptions]);

  const dlqTable = useReactTable({
    data: dlqMessages,
    columns: dlqMessageColumnDefs,
    state: { rowSelection, sorting },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getRowId: (row) => row.messageId,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 25 },
    },
  });

  if (isLoading && subscriptions.length === 0) {
    return <PageLoader message="Loading Pub/Sub data..." />;
  }

  return (
    <div>
      <PageHeader
        title="GCP Pub/Sub Monitoring"
        subtitle="Monitor Pub/Sub topics and subscriptions for message delays"
        action={
          <Button
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <IconRefresh className="size-4" />
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      {dashboardStatus?.overallStatus && (
        <Badge
          variant={getStatusBadgeVariant(dashboardStatus.overallStatus)}
          className="mb-2 font-semibold"
        >
          {dashboardStatus.overallStatus.toUpperCase()}
        </Badge>
      )}
      <div className="mb-2 flex flex-wrap gap-1.5">
        {error ? (
          <Badge variant="destructive">{error}</Badge>
        ) : (
          <>
            <Badge variant="default">{topics.length} topic{topics.length === 1 ? '' : 's'}</Badge>
            <Badge variant="secondary">{subscriptions.length} subscription{subscriptions.length === 1 ? '' : 's'}</Badge>
            {dashboardStatus?.alertCount !== undefined && dashboardStatus.alertCount > 0 && (
              <Badge variant="destructive">{dashboardStatus.alertCount} alert{dashboardStatus.alertCount === 1 ? '' : 's'}</Badge>
            )}
            {dashboardStatus?.warningCount !== undefined && dashboardStatus.warningCount > 0 && (
              <Badge variant="outline" className="border-amber-300 text-amber-700">{dashboardStatus.warningCount} warning{dashboardStatus.warningCount === 1 ? '' : 's'}</Badge>
            )}
            {dashboardStatus?.healthyCount !== undefined && dashboardStatus.healthyCount > 0 && (
              <Badge variant="default">{dashboardStatus.healthyCount} healthy</Badge>
            )}
          </>
        )}
      </div>
      {(dashboardStatus?.lastChecked || lastUpdatedAt) && (
        <div className="mb-3 flex gap-4">
          {dashboardStatus?.lastChecked && (
            <span className="text-xs text-muted-foreground">
              Status checked: {new Date(dashboardStatus.lastChecked).toLocaleTimeString()}
            </span>
          )}
          {lastUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Last refreshed: {lastUpdatedAt.toLocaleString()}
            </span>
          )}
        </div>
      )}

      <MainCard>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList variant="line" className="mb-3">
            <TabsTrigger value="subscriptions">Subscriptions ({subscriptions.length})</TabsTrigger>
            <TabsTrigger value="topics">Topics ({topics.length})</TabsTrigger>
            <TabsTrigger value="dlq">Dead Letter Queues ({dlqSubscriptions.length})</TabsTrigger>
          </TabsList>

          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading data…</p>
          )}

          <TabsContent value="subscriptions">
            {!isLoading && !error && (
              <div>
                {sortedSubscriptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No subscriptions found.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {sortedSubscriptions.map((sub) => {
                      const hasError = sub.backlog === undefined || sub.oldestMessageAge === undefined;
                      const isExpanded = expandedSubscriptions.has(sub.id);
                      const history = subscriptionHistories[sub.id];
                      const isLoadingHistory = loadingHistories.has(sub.id);
                      
                      return (
                        <div
                          key={sub.id}
                          className={cn(
                            'rounded-lg border p-3',
                            sub.backlogStatus === 'alert' && 'border-red-400 bg-red-50 dark:border-red-800 dark:bg-red-950',
                            sub.backlogStatus === 'warning' && 'border-amber-400 bg-amber-50 dark:border-amber-800 dark:bg-amber-950',
                            !sub.backlogStatus || sub.backlogStatus === 'healthy' ? 'border-border bg-card' : ''
                          )}
                        >
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold">{sub.id}</span>
                              {sub.backlogStatus && (
                                <Badge
                                  variant={getStatusBadgeVariant(sub.backlogStatus)}
                                  className="h-5 text-[0.65rem]"
                                >
                                  {getStatusLabel(sub.backlogStatus)}
                                </Badge>
                              )}
                            </div>
                            <span className="break-all text-xs text-muted-foreground">
                              {sub.fullName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Topic: <span className="font-mono">{sub.topic}</span>
                            </span>
                            
                            <div className="flex flex-wrap gap-4">
                              <span className="text-xs font-medium">
                                Backlog:{' '}
                                {hasError ? (
                                  <span className="text-red-600">Error fetching</span>
                                ) : (
                                  <span className={cn(sub.backlog && sub.backlog > 0 ? 'font-semibold text-amber-600' : '')}>
                                    {sub.backlog?.toLocaleString() ?? '—'}
                                  </span>
                                )}
                              </span>
                              <span className="text-xs font-medium">
                                Oldest Message Age:{' '}
                                {hasError ? (
                                  <span className="text-red-600">Error fetching</span>
                                ) : (
                                  <span className={cn(sub.oldestMessageAge && sub.oldestMessageAge > 900 ? 'font-semibold text-amber-600' : '')}>
                                    {formatAge(sub.oldestMessageAge)}
                                  </span>
                                )}
                              </span>
                            </div>
                            
                            {sub.backlog !== undefined && sub.backlog > 0 && (
                              <Alert className="border-amber-300 bg-amber-50 py-1.5 dark:border-amber-700 dark:bg-amber-950">
                                <AlertDescription className="text-amber-800 dark:text-amber-200">
                                  ⚠️ Messages pending processing
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              className="self-start"
                              onClick={() => toggleSubscriptionExpanded(sub.id)}
                            >
                              {isExpanded ? <IconChevronDown className="size-4" /> : <IconChevronRight className="size-4" />}
                              {isExpanded ? 'Hide History' : 'Show Last Hour History'}
                            </Button>
                            
                            {isExpanded && (
                              <div className="mt-2 border-t border-border pt-3">
                                {isLoadingHistory ? (
                                  <span className="text-xs text-muted-foreground">Loading history...</span>
                                ) : history ? (
                                  <div className="flex flex-col gap-4">
                                    <div>
                                      <span className="text-xs font-semibold">Backlog (Last Hour)</span>
                                      <div className="h-[200px] min-w-[600px] w-full">
                                        <ChartContainer
                                          config={
                                            {
                                              backlog: { label: 'Backlog', color: '#ff9800' },
                                            } satisfies ShadcnChartConfig
                                          }
                                          className="h-[200px] w-full"
                                        >
                                          <LineChart
                                            data={history.backlog.map((d) => ({
                                              t: d.timestamp,
                                              backlog: d.value,
                                            }))}
                                            margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
                                          >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis
                                              dataKey="t"
                                              type="number"
                                              domain={['dataMin', 'dataMax']}
                                              tickFormatter={(v) => new Date(v).toLocaleTimeString()}
                                              tick={{ fontSize: 11 }}
                                            />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            <Line
                                              type="monotone"
                                              dataKey="backlog"
                                              stroke="var(--color-backlog)"
                                              strokeWidth={2}
                                              dot={false}
                                            />
                                          </LineChart>
                                        </ChartContainer>
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-xs font-semibold">Oldest Message Age (Last Hour)</span>
                                      <div className="h-[200px] min-w-[600px] w-full">
                                        <ChartContainer
                                          config={
                                            {
                                              age: { label: 'Age (seconds)', color: '#f44336' },
                                            } satisfies ShadcnChartConfig
                                          }
                                          className="h-[200px] w-full"
                                        >
                                          <LineChart
                                            data={history.oldestMessageAge.map((d) => ({
                                              t: d.timestamp,
                                              age: d.value,
                                            }))}
                                            margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
                                          >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis
                                              dataKey="t"
                                              type="number"
                                              domain={['dataMin', 'dataMax']}
                                              tickFormatter={(v) => new Date(v).toLocaleTimeString()}
                                              tick={{ fontSize: 11 }}
                                            />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            <Line
                                              type="monotone"
                                              dataKey="age"
                                              stroke="var(--color-age)"
                                              strokeWidth={2}
                                              dot={false}
                                            />
                                          </LineChart>
                                        </ChartContainer>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">No history data available</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="topics">
            {!isLoading && !error && (
              <div>
                {topics.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No topics found.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {topics.map((topic) => (
                      <div key={topic.name} className="rounded-lg border border-border p-3">
                        <span className="text-sm font-semibold">{topic.name}</span>
                        <p className="break-all text-xs text-muted-foreground">{topic.fullName}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="dlq">
            {!isLoading && !error && (
              <div>
                {dlqSubscriptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No Dead Letter Queues found.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-sm text-muted-foreground">
                      Select a DLQ to view and manage messages:
                    </p>
                    <div className="flex flex-col gap-2">
                      {dlqSubscriptions.map((dlq) => (
                        <div
                          key={dlq.id}
                          className={cn(
                            'cursor-pointer rounded-lg border p-3 transition-colors hover:border-primary hover:bg-primary/5',
                            selectedDlq === dlq.id ? 'border-primary bg-primary/5' : 'border-border'
                          )}
                          onClick={() => {
                            setSelectedDlq(dlq.id);
                            fetchDlqMessages(dlq.id);
                          }}
                        >
                          <span className="text-sm font-semibold">{dlq.id}</span>
                          <p className="text-xs text-muted-foreground">
                            Original Topic: {dlq.originalTopic}
                          </p>
                        </div>
                      ))}
                    </div>
                    
                    {selectedDlq && (
                      <div className="mt-3 border-t border-border pt-4">
                        <h3 className="mb-2 text-base font-semibold">
                          Messages in {selectedDlq}
                        </h3>
                        {loadingDlqMessages ? (
                          <p className="text-sm text-muted-foreground">Loading messages...</p>
                        ) : dlqMessages.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No messages in this DLQ.</p>
                        ) : (
                          <div>
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm text-muted-foreground">
                                {dlqMessages.length} message(s) | {selectedMessageIds.size} selected
                                {!hasWriteAccess('monitoring') && ' | No write access'}
                              </span>
                              <div className="flex gap-1.5">
                                {hasWriteAccess('monitoring') ? (
                                  <>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      disabled={selectedMessageIds.size === 0 || isProcessing}
                                      onClick={() => {
                                        console.log('Acknowledge clicked', { selectedMessageIds: Array.from(selectedMessageIds), isProcessing });
                                        handleAcknowledge();
                                      }}
                                    >
                                      Acknowledge ({selectedMessageIds.size})
                                    </Button>
                                    <Button
                                      size="sm"
                                      disabled={selectedMessageIds.size === 0 || isProcessing}
                                      onClick={() => {
                                        console.log('Reprocess clicked', { selectedMessageIds: Array.from(selectedMessageIds), isProcessing });
                                        handleReprocess();
                                      }}
                                    >
                                      Reprocess ({selectedMessageIds.size})
                                    </Button>
                                  </>
                                ) : (
                                  <span className="text-xs text-red-600">Write access required</span>
                                )}
                              </div>
                            </div>
                            <div className="w-full">
                              <div className="max-h-[600px] w-full overflow-auto rounded-lg border border-border">
                                <ShadTable>
                                  <TableHeader>
                                    {dlqTable.getHeaderGroups().map((headerGroup) => (
                                      <ShadTableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                          <TableHead
                                            key={header.id}
                                            style={{
                                              width: header.getSize(),
                                              minWidth: header.getSize(),
                                            }}
                                          >
                                            {header.isPlaceholder
                                              ? null
                                              : flexRender(header.column.columnDef.header, header.getContext())}
                                          </TableHead>
                                        ))}
                                      </ShadTableRow>
                                    ))}
                                  </TableHeader>
                                  <ShadTableBody>
                                    {dlqTable.getRowModel().rows.map((row) => (
                                      <ShadTableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() ? 'selected' : undefined}
                                      >
                                        {row.getVisibleCells().map((cell) => (
                                          <ShadTableCell key={cell.id} className="max-w-0">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </ShadTableCell>
                                        ))}
                                      </ShadTableRow>
                                    ))}
                                  </ShadTableBody>
                                </ShadTable>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-muted-foreground">Rows per page</span>
                                  <select
                                    value={dlqTable.getState().pagination.pageSize}
                                    onChange={(e) => dlqTable.setPageSize(Number(e.target.value))}
                                    className="min-w-[72px] rounded-lg border border-border bg-card px-2 py-1 text-sm"
                                  >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                  </select>
                                </div>
                                <Pagination table={dlqTable} />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </MainCard>
      <MessageDetailModal />
      <ReprocessResultDialog />
    </div>
  );
}
